import {
	type DefaultMessagesResponse,
	type PlacementMessageFrontendFactory,
	type RML,
	type AnyProps,
	type NoProps,
	type PlacementRenderer,
	type AnyFrontendStage,
	type PlacementBrowserConfig,
} from '@post-office/message-maker-browser/types';

import { useInitialData } from '@post-office/message-maker-browser/use-initial-data';

import { createPlacementFrontend } from '@post-office/message-maker-browser/create-placement-frontend';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box } from '@atlaskit/primitives/box';

import { xcss } from '@atlaskit/primitives/xcss/xcss';

import { useMessagePlacementHooks } from '@atlassian/post-office-context/message-placement';

import {
	MessageContextProvider,
	useMessageContext,
} from '@atlassian/post-office-context/message-context';

import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';

import { usePlacementApi } from '@post-office/use-placement-api';

import { useIntl } from 'react-intl';

import { ActivationChecklistShell } from './activation-checklist-shell';

import { activationChecklistMessages } from './copy';

import {
	type ActivationChecklistItemClickHandler,
	type ActivationChecklistItemsRenderedHandler,
	type ChecklistRenderMessage,
	type ServerResolvedMessage,
	resolveDismissibleMessages,
	resolvePlacementMessages,
	resolveVisibleMessages,
} from './runtime';

import type { ActivationChecklistProduct, ActivationChecklistRuntimeContext } from './types';

export { ChecklistItemCard } from './message-templates/checklist-item';

export type { ChecklistItemCardProps } from './message-templates/checklist-item';

export {
	resolveDismissibleMessages,
	resolvePlacementMessages,
	resolveVisibleMessages,
} from './runtime';

export type {
	ActivationChecklistItemClickHandler,
	ActivationChecklistItemsRenderedHandler,
	ChecklistRenderMessage,
	ServerResolvedMessage,
} from './runtime';

export type { ActivationChecklistProduct, ActivationChecklistRuntimeContext } from './types';

const placementId = 'wac-product-activation-checklist';

const EMPTY_MESSAGES: readonly ServerResolvedMessage[] = [];

const checklistListStyles = xcss({
	listStyle: 'none',
	margin: 'space.0',
	padding: 'space.0',
});

const placementConfig: PlacementBrowserConfig = {
	placementId,
	options: {
		choreographer: {
			isDefaultEnabled: false,
		},
	},
};

const placement = {
	createFrontend: createPlacementFrontend(placementConfig),
};

export const createFrontend: <U extends AnyProps = NoProps>(
	placementRenderer: PlacementRenderer<
		{
			render: AnyFrontendStage;
		},
		U
	>,
) => PlacementMessageFrontendFactory<
	{
		render: AnyFrontendStage;
	},
	U
> = placement.createFrontend;

type PlacementProps = {
	product: ActivationChecklistProduct;
	targets?: ActivationChecklistRuntimeContext;
	onItemClick?: ActivationChecklistItemClickHandler;
	onItemsRendered?: ActivationChecklistItemsRenderedHandler;
};

type PlacementFrontendProps = PlacementProps & RML;

type MessagePlacementHooks = ReturnType<typeof useMessagePlacementHooks>;

type MessageDismissHandler = Parameters<MessagePlacementHooks['onMessageDismiss']>[0];

type OnMessageDismiss = (callback: MessageDismissHandler) => () => void;

type DismissEventErrorHandler = (error: unknown, messageInstanceId: string) => void;

type DismissInteraction = () => void;

type RegisterDismissInteraction = (
	messageInstanceId: string,
	dismissInteraction: DismissInteraction,
) => () => void;

const reportDismissEventError: DismissEventErrorHandler = (error) => {
	if (typeof globalThis.reportError === 'function') {
		globalThis.reportError(error);
	}
};

const useDismissedMessageIds = (onMessageDismiss: OnMessageDismiss): ReadonlySet<string> => {
	const [dismissedMessageIds, setDismissedMessageIds] = useState<ReadonlySet<string>>(
		() => new Set(),
	);

	useEffect(
		() =>
			onMessageDismiss(({ messageInstanceId }) => {
				setDismissedMessageIds((currentDismissedMessageIds) => {
					if (currentDismissedMessageIds.has(messageInstanceId)) {
						return currentDismissedMessageIds;
					}

					return new Set([...currentDismissedMessageIds, messageInstanceId]);
				});
			}),
		[onMessageDismiss],
	);

	return dismissedMessageIds;
};

const useChecklistMessages = ({
	dismissedMessageIds,
	isLoading,
	messages,
	product,
	targets,
}: {
	dismissedMessageIds: ReadonlySet<string>;
	isLoading: boolean;
	messages: readonly ServerResolvedMessage[];
	product: ActivationChecklistProduct;
	targets: ActivationChecklistRuntimeContext;
}) => {
	const resolvedMessages = useMemo(() => resolvePlacementMessages(messages), [messages]);
	const dismissibleMessages = useMemo(
		() => resolveDismissibleMessages(resolvedMessages, product, dismissedMessageIds),
		[dismissedMessageIds, product, resolvedMessages],
	);
	const visibleMessages = useMemo(
		() => (isLoading ? [] : resolveVisibleMessages(dismissibleMessages, product, targets)),
		[dismissibleMessages, isLoading, product, targets],
	);

	return { dismissibleMessages, visibleMessages };
};

export const useDismissInteractionRegistry = (): {
	dismissMessageInteraction: (messageInstanceId: string) => void;
	registerDismissInteraction: RegisterDismissInteraction;
} => {
	const dismissInteractions = useRef(new Map<string, DismissInteraction>());
	const registerDismissInteraction = useCallback<RegisterDismissInteraction>(
		(messageInstanceId, dismissInteraction) => {
			dismissInteractions.current.set(messageInstanceId, dismissInteraction);
			return () => {
				if (dismissInteractions.current.get(messageInstanceId) === dismissInteraction) {
					dismissInteractions.current.delete(messageInstanceId);
				}
			};
		},
		[],
	);
	const dismissMessageInteraction = useCallback((messageInstanceId: string) => {
		dismissInteractions.current.get(messageInstanceId)?.();
	}, []);
	return { dismissMessageInteraction, registerDismissInteraction };
};

const DismissMessageInteraction = ({
	messageInstanceId,
	registerDismissInteraction,
}: {
	messageInstanceId: string;
	registerDismissInteraction: RegisterDismissInteraction;
}): null => {
	const { onClosed } = usePostOfficeUserInteractions();
	useEffect(
		() =>
			registerDismissInteraction(messageInstanceId, () => {
				void onClosed({ interactionIdentifier: 'dismiss' });
			}),
		[messageInstanceId, onClosed, registerDismissInteraction],
	);
	return null;
};

export const DismissMessageInteractionRegistrars = ({
	dismissibleMessages,
	registerDismissInteraction,
}: {
	dismissibleMessages: readonly ServerResolvedMessage[];
	registerDismissInteraction: RegisterDismissInteraction;
}): JSX.Element => {
	const messageContext = useMessageContext();
	return (
		<>
			{dismissibleMessages.map((message) => (
				<MessageContextProvider
					key={message.messageInstanceId}
					value={{
						...messageContext,
						messageInstanceId: message.messageInstanceId,
						messageTemplateId: message.messageTemplateId,
						messageCategory: message.messageCategory,
						triggerId: message.triggerId,
						messageCreationType: message.messageCreationType,
					}}
				>
					<DismissMessageInteraction
						messageInstanceId={message.messageInstanceId}
						registerDismissInteraction={registerDismissInteraction}
					/>
				</MessageContextProvider>
			))}
		</>
	);
};

export const useDismissChecklist = ({
	dismissibleMessages,
	dismissMessageFromPlacement,
	onDismissEventError = reportDismissEventError,
	dismissMessageInteraction,
}: {
	dismissibleMessages: readonly ServerResolvedMessage[];
	dismissMessageFromPlacement: MessagePlacementHooks['dismissMessageFromPlacement'];
	onDismissEventError?: DismissEventErrorHandler;
	dismissMessageInteraction: (messageInstanceId: string) => void;
}): (() => void) =>
	useCallback(() => {
		for (const message of dismissibleMessages) {
			try {
				dismissMessageInteraction(message.messageInstanceId);
			} catch (error) {
				onDismissEventError(error, message.messageInstanceId);
			}
			try {
				dismissMessageFromPlacement({ messageInstanceId: message.messageInstanceId });
			} catch (error) {
				onDismissEventError(error, message.messageInstanceId);
			}
		}
	}, [
		dismissibleMessages,
		dismissMessageFromPlacement,
		dismissMessageInteraction,
		onDismissEventError,
	]);

export const useReportChecklistRender = ({
	visibleMessages,
	onItemsRendered,
}: {
	visibleMessages: readonly ChecklistRenderMessage[];
	onItemsRendered?: ActivationChecklistItemsRenderedHandler;
}): void => {
	const hasReportedRender = useRef(false);

	useEffect(() => {
		if (hasReportedRender.current || visibleMessages.length === 0 || !onItemsRendered) {
			return;
		}

		hasReportedRender.current = true;
		onItemsRendered(
			visibleMessages.map((message) => ({ id: message.id, completed: message.completed })),
		);
	}, [onItemsRendered, visibleMessages]);
};

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: AnyFrontendStage;
	},
	PlacementFrontendProps
> = createFrontend<PlacementFrontendProps>(
	(Messages) =>
		({ product, targets = {}, onItemClick, onItemsRendered, initialData }) => {
			const intl = useIntl();
			const { dismissMessageFromPlacement, onMessageDismiss } = useMessagePlacementHooks();
			const { dismissMessageInteraction, registerDismissInteraction } =
				useDismissInteractionRegistry();
			const hasInitialData = typeof initialData !== 'undefined';
			const initialPlacementData =
				useInitialData<DefaultMessagesResponse<ServerResolvedMessage>>(initialData);
			const fallbackPlacementData = usePlacementApi<DefaultMessagesResponse<ServerResolvedMessage>>(
				'/',
				{
					autoRefetch: {
						isEnabled: !hasInitialData,
						updateKey: hasInitialData ? 'initial-data' : 'placement-api',
					},
				},
			);
			const { data, error, isLoading } = hasInitialData
				? initialPlacementData
				: fallbackPlacementData;

			const messages = data?.messages ?? EMPTY_MESSAGES;
			const dismissedMessageIds = useDismissedMessageIds(onMessageDismiss);
			const { dismissibleMessages, visibleMessages } = useChecklistMessages({
				dismissedMessageIds,
				isLoading,
				messages,
				product,
				targets,
			});
			const hasVisibleMessages = visibleMessages.length > 0;

			useReportChecklistRender({ visibleMessages, onItemsRendered });

			const dismissLabel = intl.formatMessage(activationChecklistMessages.dismissChecklist);
			const dismissChecklist = useDismissChecklist({
				dismissibleMessages,
				dismissMessageFromPlacement,
				dismissMessageInteraction,
			});

			if (error) {
				throw error;
			}

			if (!hasVisibleMessages) {
				return null;
			}

			return (
				<ActivationChecklistShell dismissLabel={dismissLabel} onDismiss={dismissChecklist}>
					<DismissMessageInteractionRegistrars
						dismissibleMessages={dismissibleMessages}
						registerDismissInteraction={registerDismissInteraction}
					/>
					<Box as="ul" role="list" xcss={checklistListStyles}>
						{visibleMessages.map((message, index) => (
							<Messages.render
								key={message.messageInstanceId}
								{...message}
								id={message.id}
								completed={message.completed}
								actionType={message.actionType}
								cta={message.cta ? { href: message.cta.href } : undefined}
								hasTopBorder={index > 0}
								onClick={
									onItemClick
										? () => onItemClick({ id: message.id, actionType: message.actionType })
										: undefined
								}
							/>
						))}
					</Box>
				</ActivationChecklistShell>
			);
		},
);

export {
	resolveActivationChecklistHref,
	shouldShowActivationChecklistMessage,
	sortMessagesByOrder,
} from './runtime';
